import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Search } from 'lucide-react'
import { fetchStocks } from '@/services/screenerApi'

export const MultiSelect = ({
    value = [], // Array of stock objects: { id, symbol, name, ... }
    onChange, // Function to handle value changes
    placeholder = 'Search symbols...',
    maxCount = 15, // Max results to show in dropdown
    className,
    disabled = false
}) => {
    // Stock search states
    const [searchQuery, setSearchQuery] = useState('')
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
    
    const inputRef = useRef(null)
    const dropdownRef = useRef(null)

    // Debounced stock search
    useEffect(() => {
        const trimmedQuery = searchQuery.trim()

        if (trimmedQuery === '') {
            setResults([])
            setShowDropdown(false)
            return
        }

        const timeoutId = setTimeout(async () => {
            setLoading(true)
            try {
                const data = await fetchStocks(trimmedQuery)
                setResults(data)
                setShowDropdown(true)
            } catch (err) {
                console.error('Failed to fetch stocks:', err)
                setResults([])
                setShowDropdown(false)
            } finally {
                setLoading(false)
            }
        }, 400) // 400ms debounce

        return () => clearTimeout(timeoutId)
    }, [searchQuery])

    // Handle stock selection
    const handleSelect = (stock) => {
        // Prevent duplicates
        if (value.some(s => s.symbol === stock.symbol)) {
            setSearchQuery('')
            setShowDropdown(false)
            return;
        }

        const newStocks = [...value, stock]
        onChange(newStocks)
        setSearchQuery('')
        setShowDropdown(false)
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    // Handle stock removal
    const handleRemove = (symbolToRemove) => {
        if (disabled) return;
        const newStocks = value.filter(s => s.symbol !== symbolToRemove)
        onChange(newStocks)
    }

    // Filter out selected stocks from results
    const availableStocks = results
        .filter(stock => !value.some(selected => selected.symbol === stock.symbol))
        .slice(0, maxCount)

    // Calculate dropdown position based on input element
    const updateDropdownPosition = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect()
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4, // 4px gap
                left: rect.left + window.scrollX,
                width: rect.width
            })
        }
    }

    // Update dropdown position when it should be shown
    useEffect(() => {
        if (showDropdown && inputRef.current) {
            updateDropdownPosition()
        }
    }, [showDropdown, searchQuery, results])

    // Update position on scroll and resize
    useEffect(() => {
        if (!showDropdown) return

        const handleUpdate = () => {
            updateDropdownPosition()
        }

        window.addEventListener('scroll', handleUpdate, true)
        window.addEventListener('resize', handleUpdate)

        return () => {
            window.removeEventListener('scroll', handleUpdate, true)
            window.removeEventListener('resize', handleUpdate)
        }
    }, [showDropdown])

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowDropdown(false)
            }
        }

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showDropdown])

    return (
        <div className={`space-y-3 ${className || ''}`}>
            {/* Selected Stocks Tags */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {value.map((stock, index) => (
                        <span
                            key={stock.id || stock.symbol || index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                            {stock.full_symbol || stock.symbol}
                            <button
                                type="button"
                                onClick={() => handleRemove(stock.symbol)}
                                disabled={disabled}
                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors disabled:cursor-not-allowed"
                                aria-label={`Remove ${stock.full_symbol || stock.symbol}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Stock Search Input */}
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                        if (availableStocks.length > 0 && searchQuery.trim() !== '') {
                            setShowDropdown(true)
                        }
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                
                {/* Search Icon or Loading Spinner */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    {loading ? (
                       <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ) : (
                       <Search size={18} />
                    )}
                </div>

                {/* Dropdown Results - Rendered via Portal */}
                {showDropdown && createPortal(
                    <div
                        ref={dropdownRef}
                        className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto"
                        style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`
                        }}
                    >
                        {availableStocks.length > 0 ? (
                            <>
                                {availableStocks.map((stock) => (
                                    <button
                                        key={stock.id}
                                        type="button"
                                        onClick={() => handleSelect(stock)}
                                        className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors border-b border-gray-50 last:border-b-0"
                                    >
                                        <div className="font-medium text-gray-900 flex justify-between">
                                            <span>{stock.full_symbol || stock.symbol}</span>
                                            <span className="text-gray-400 text-xs font-normal self-center">{stock.exchange_name || 'US'}</span>
                                        </div>
                                        {stock.name && stock.name !== stock.symbol && (
                                            <div className="text-xs text-gray-500 truncate">{stock.name}</div>
                                        )}
                                    </button>
                                ))}
                                {results.length > maxCount && (
                                    <div className="px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-200 bg-gray-50">
                                        Showing top {maxCount} results...
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                {loading ? 'Searching...' : 'No stocks found'}
                            </div>
                        )}
                    </div>,
                    document.body
                )}
            </div>
        </div>
    )
}

export default MultiSelect

